/**
 * @fileoverview "Plano e limites" de uma Empresa, para o super: o modal que abre
 * pelo botão ao lado de "Editar", no detalhe da Empresa (/companies). Mostra o
 * plano e o que foi contratado, a regra dos limites, o uso de cada recurso contra
 * o limite e as exceções e cortesias; o histórico de mudanças abre num painel ao
 * lado. "Alterar plano" e "Conceder exceção" trocam o conteúdo do modal (sem
 * modal sobre modal) e "Revogar" pede o motivo na própria linha. Toda mudança
 * pede motivo e fica no histórico. O fundo, os botões e os campos vêm da tela de
 * Empresas (prop `classes`). Abre no fim da página (portal): no celular, por
 * cima da barra de navegação de baixo, e os menus dos selects por cima dele.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { makeStyles } from "@material-ui/core/styles";
import { MenuItem, Tooltip } from "@material-ui/core";
import { ArrowLeft, ChevronDown, History, Info, X } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ModernSelect from "../ModernSelect";
import { MeterBar, meterState } from "./meters";

const PLAN_LABELS = { starter: "Talk Starter", basic: "Talk Basic", pro: "Talk Pro" };
const MODE_LABELS = { off: "Desligados", shadow: "Só observando", enforce: "Ligados" };
const MODE_HELP = {
  off: "Os limites do plano não são conferidos.",
  shadow: "Os limites são conferidos, mas nada é recusado: quem passaria do limite só fica registrado.",
  enforce: "Passou do limite do plano, o sistema recusa criar pessoa, conta, número ou quadro."
};
const RESOURCES = [
  { key: "users", label: "Pessoas", help: "Pessoas com acesso em qualquer conta da Empresa. A mesma pessoa em várias contas conta uma vez. A IA não conta." },
  { key: "internalChatUsers", label: "Chat Interno", help: "Pessoas com o Chat Interno ligado (acesso Completo ou Apenas Interno)." },
  { key: "accounts", label: "Contas", help: "Contas ativas da Empresa." },
  { key: "whatsapps", label: "Números", help: "Números de WhatsApp de todas as contas. O LiveChat não conta." },
  { key: "crmBoards", label: "Quadros do CRM", help: "Quadros de todas as contas. Cada conta tem a vaga do seu quadro Principal guardada." }
];
const MESSAGE_RESOURCES = [
  { key: "commonMessages", label: "Mensagens comuns por mês" },
  { key: "aiMessages", label: "Mensagens da IA por mês" }
];
const MESSAGE_USAGE = [
  { key: "common", label: "Comuns", help: "Mensagens que atendentes, chatbots, fluxos e automações enviam aos clientes, pelo WhatsApp e pelo LiveChat. Recebidas, reações e edições não contam." },
  { key: "ai", label: "Da IA", help: "Mensagens que a IA envia aos clientes. Não gastam as comuns. Acabaram, a IA para e a conversa vai para os atendentes." }
];
const LIMIT_ITEMS = [...RESOURCES, ...MESSAGE_RESOURCES];
const COURTESY_ITEMS = [
  { key: "all", label: "Tudo (cortesia total)" },
  { key: "plan", label: "Plano" },
  { key: "internal_chat", label: "Chat Interno" },
  { key: "ai_pack", label: "Pacote de IA" },
  { key: "user_license", label: "Licença de uma pessoa" }
];
// Etapa 11: o desconto vale num item ou na mensalidade toda; o preço negociado, por unidade de um item exato
// (as licenças de um plano, o tamanho de um pacote de IA: mudou o plano ou o pacote, ele deixa de valer).
const DISCOUNT_ITEMS = [
  { key: "all", label: "A mensalidade toda" },
  { key: "plan", label: "Licença do plano" },
  { key: "internal_chat", label: "Licença do Chat Interno" },
  { key: "ai_pack", label: "Pacote de IA" }
];
const DEFAULT_ITEM = { limit: "whatsapps", courtesy: "all", discount: "all" };
/** As opções do preço negociado: "plan:pro", "internal_chat", "ai_pack:10000". */
const priceItems = (summary) => [
  ...Object.values(summary.catalog.plans)
    .filter((plan) => plan.pricePerUserCents > 0)
    .map((plan) => ({ key: `plan:${plan.key}`, label: `Licença do ${plan.name}`, cents: plan.pricePerUserCents })),
  { key: "internal_chat", label: "Licença do Chat Interno", cents: summary.catalog.internalChatPriceCents },
  ...summary.catalog.aiPacks.map((pack) => ({ key: `ai_pack:${pack.messages}`, label: `Pacote de IA de ${number(pack.messages)} mensagens`, cents: pack.priceCents }))
];
/** O preço começa pelo plano (ou pacote) que a Empresa tem. */
const defaultPriceItem = (summary) => {
  const options = priceItems(summary);
  const current = summary.group.plan && options.find((option) => option.key === `plan:${summary.group.plan}`);
  return (current || options[0]).key;
};
/** "79,90", "1.234,56" ou "79.90" em centavos (nulo: não é um valor). */
const centsOf = (text) => {
  const raw = String(text).trim();
  // Sem vírgula, um ponto com 1 ou 2 casas no fim é a vírgula decimal ("79.90"); senão, o ponto separa milhares.
  const clean = !raw.includes(",") && /^\d+\.\d{1,2}$/.test(raw) ? raw : raw.replace(/\./g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return null;
  return Math.round(Number(clean) * 100);
};
const percentOf = (text) => (/^\d{1,3}$/.test(String(text).trim()) && Number(text) >= 1 && Number(text) <= 100 ? Number(text) : null);
const STATUS_LABELS = { active: "Valendo", scheduled: "Agendada", expired: "Encerrada", revoked: "Revogada" };
const ACTION_LABELS = {
  "commercial.updated": "Plano alterado",
  "exception.granted": "Exceção concedida",
  "exception.revoked": "Exceção revogada",
  "courtesy.granted": "Cortesia concedida",
  "company_group.split": "Conta separada de outra Empresa",
  "company_group.deleted": "Empresa apagada",
  "company_group.created": "Empresa criada",
  "billing.subscribed": "Contratação paga",
  "billing.changed": "Alteração paga",
  "billing.reduction_scheduled": "Redução agendada",
  "billing.reduction_canceled": "Redução desfeita",
  "billing.reduced": "Redução aplicada na renovação",
  "billing.reduction_review": "Redução para o suporte",
  "billing.reduction_postponed": "Redução adiada",
  "billing.cancellation_scheduled": "Cancelamento agendado",
  "billing.cancellation_undone": "Cancelamento desfeito",
  "billing.cancellation_review": "Cancelamento para o suporte",
  "billing.cancellation_postponed": "Cancelamento adiado",
  "billing.canceled_to_starter": "Cancelamento aplicado: Starter",
  "billing.adequacy_chosen": "Escolha do que fica",
  "billing.adequacy_done": "Escolha do que fica concluída",
  "exception.expired": "Exceção encerrada (fim do prazo)",
  "exception.adequacy_due": "Prazo para escolher o que fica",
  "exception.adequacy_restricted": "Acesso restrito: o prazo da escolha passou",
  "billing.archive_restored": "Item restaurado do arquivo",
  "billing.suspended": "Suspensa pelo atraso no pagamento",
  "billing.suspension_lifted": "Suspensão retirada",
  "billing.double_payment": "Pagamento em dobro (estornar um)",
  "billing.dispute_opened": "Contestação no cartão aberta",
  "billing.dispute_won": "Contestação ganha",
  "billing.dispute_lost": "Contestação perdida (cobrar de novo)",
  "billing.subscription_canceled_outside": "Assinatura cancelada fora do Talk",
  "billing.refund_requested": "Estorno pedido",
  "billing.refund_refused": "Estorno recusado",
  "billing.refund_approved": "Estorno aprovado",
  "billing.refunded": "Estornado",
  "billing.annulment_scheduled": "Anulação: Starter agendado",
  "billing.annulment_manual": "Anulação: ajustar o plano",
  "billing.annulment_failed": "Anulação não concluída",
  "billing.refund_canceled": "Aprovação do estorno desfeita"
};
const REFUND_STATUS = {
  requested: "Pedido: falta decidir",
  refused: "Recusado",
  approved: "Aprovado: a Pagar.me não estornou",
  refunding: "Devolvendo: falta a Pagar.me confirmar",
  refunded: "Estornado",
  canceled: "Aprovação desfeita"
};
const REFUND_KIND = { correction: "correção", annulment: "anulação" };
const DISPUTE_LABELS = { open: "Em análise", won: "Ganha: o valor ficou com a Sttelar", lost: "Perdida: cobrada de novo" };
const SCOPE_LABELS = { all: "o envio de mensagens e o Chat Interno estão parados", internal_chat: "o Chat Interno está parado" };
const NONE = "none";

const number = (value) => Number(value).toLocaleString("pt-BR");
const money = (cents) => (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
// O prazo das exceções vale pelo dia de São Paulo (regra do servidor), visto de qualquer fuso.
const date = (value) => new Date(value).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
const dateTime = (value) => new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
const limitText = (limit) => (limit == null ? "Sem limite" : number(limit));
// O uso e o limite de um recurso: "1 de 2" ou "1, sem limite".
const usedOfLimit = (used, limit) => `${number(used)}${limit == null ? ", " : " de "}${limitText(limit).toLowerCase()}`;
const sentence = (parts) => {
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} e ${parts[parts.length - 1]}` : parts[0];
  return `${list.charAt(0).toUpperCase()}${list.slice(1)}.`;
};
const itemLabel = (exception) => {
  if (exception.kind === "limit") {
    const item = LIMIT_ITEMS.find((i) => i.key === exception.item);
    return `${item ? item.label : exception.item}: ${limitText(exception.value)}`;
  }
  if (exception.kind === "price") {
    const what =
      exception.item === "internal_chat"
        ? "licença do Chat Interno"
        : exception.item === "ai_pack"
          ? `pacote de IA de ${number(exception.appliesTo || 0)} mensagens`
          : `licença do ${exception.appliesTo === "basic" ? "Talk Basic" : exception.appliesTo === "pro" ? "Talk Pro" : exception.appliesTo || "plano"}`;
    return `Preço negociado: ${what} a ${money(exception.value || 0)}`;
  }
  if (exception.kind === "discount") {
    const item = DISCOUNT_ITEMS.find((i) => i.key === exception.item);
    return `Desconto de ${exception.value}%: ${item ? item.label.toLowerCase() : exception.item}`;
  }
  const item = COURTESY_ITEMS.find((i) => i.key === exception.item);
  return `Cortesia: ${item ? item.label.toLowerCase() : exception.item}`;
};
/** O preço do catálogo da opção escolhida, para o super comparar com o negociado. */
const listPrice = (summary, key) => {
  const option = priceItems(summary).find((o) => o.key === key);
  return option ? option.cents : 0;
};
const validity = (exception) => {
  if (exception.startsAt && exception.endsAt) return `De ${date(exception.startsAt)} a ${date(exception.endsAt)}`;
  if (exception.endsAt) return `Até ${date(exception.endsAt)}`;
  if (exception.startsAt) return `A partir de ${date(exception.startsAt)}`;
  return "Sem prazo";
};
const isEnded = (exception) => exception.status === "expired" || exception.status === "revoked";

/** O acordo numa frase: desde quando, licenças, Chat Interno e pacote de IA. */
const contractText = (group, plans) => {
  if (!group.plan) return "Como antes dos planos: os limites não são conferidos.";
  const plan = plans[group.plan];
  const parts = [
    plan.maxUsers == null ? `${number(Math.max(group.seatsContracted || 0, plan.minBilledUsers))} licenças` : `até ${number(plan.maxUsers)} pessoas`,
    group.internalChatSeats ? `${number(group.internalChatSeats)} de Chat Interno` : "sem Chat Interno"
  ];
  if (group.aiPackMessages) parts.push(`pacote de IA de ${number(group.aiPackMessages)} mensagens`);
  return `${group.planStartedAt ? `Desde ${date(group.planStartedAt)}. ` : ""}${sentence(parts)}`;
};

/** Os limites que valeriam com a escolha do formulário (o mesmo cálculo do servidor). */
const previewLimits = (form, summary) => {
  const plan = form.plan !== NONE ? summary.catalog.plans[form.plan] : null;
  const limits = {};
  LIMIT_ITEMS.forEach(({ key }) => { limits[key] = null; });
  if (plan) {
    const seats = form.seatsContracted === "" ? 0 : Number(form.seatsContracted);
    limits.users = plan.maxUsers != null ? plan.maxUsers : Math.max(seats, plan.minBilledUsers);
    limits.internalChatUsers = form.internalChatSeats === "" ? 0 : Number(form.internalChatSeats);
    limits.accounts = plan.accounts;
    limits.whatsapps = plan.whatsapps;
    limits.crmBoards = plan.crmBoards;
    limits.commonMessages = plan.commonMessages;
    limits.aiMessages = plan.aiPacks && form.aiPackMessages !== NONE ? Number(form.aiPackMessages) : plan.aiMessages;
  }
  summary.exceptions
    .filter((e) => e.kind === "limit" && e.status === "active")
    .slice()
    .reverse()
    .forEach((e) => { limits[e.item] = e.value; });
  return limits;
};

const planForm = (summary) => ({
  plan: summary.group.plan || NONE,
  enforcementMode: summary.group.enforcementMode,
  seatsContracted: summary.group.seatsContracted != null ? String(summary.group.seatsContracted) : "",
  internalChatSeats: summary.group.internalChatSeats != null ? String(summary.group.internalChatSeats) : "",
  aiPackMessages: summary.group.aiPackMessages != null ? String(summary.group.aiPackMessages) : NONE,
  reason: ""
});
const grantForm = () => ({ kind: "limit", item: "whatsapps", value: "", userId: "", startsAt: "", endsAt: "", reason: "" });

const historyDetail = (entry) => {
  const parts = [];
  if (entry.action === "commercial.updated" && entry.after) {
    const was = entry.before && entry.before.plan ? PLAN_LABELS[entry.before.plan] : "Sem plano";
    const now = entry.after.plan ? PLAN_LABELS[entry.after.plan] : "Sem plano";
    parts.push(was === now ? now : `${was} para ${now}`);
    parts.push(`Limites ${MODE_LABELS[entry.after.enforcementMode].toLowerCase()}`);
  }
  if ((entry.action === "exception.granted" || entry.action === "courtesy.granted") && entry.after) parts.push(itemLabel(entry.after));
  if ((entry.action === "exception.revoked" || entry.action === "exception.expired") && entry.before) parts.push(itemLabel(entry.before));
  if (entry.action === "exception.adequacy_due" && entry.after && entry.after.dueAt) parts.push(`Escolha até ${date(entry.after.dueAt)}`);
  if ((entry.action === "exception.adequacy_due" || entry.action === "exception.adequacy_restricted") && entry.after && Array.isArray(entry.after.blockers)) {
    parts.push(blockersText(entry.after.blockers));
  }
  return parts.join(". ");
};

// O "antes e depois" de cada entrada (etapa 11): os campos que a trilha guarda, com nome e valor legíveis.
const FIELD_LABELS = {
  plan: "Plano",
  seats: "Licenças",
  seatsContracted: "Licenças",
  internalChatSeats: "Chat Interno",
  aiPackMessages: "Pacote de IA",
  enforcementMode: "Limites",
  kind: "Tipo",
  item: "Item",
  value: "Valor",
  appliesTo: "Para",
  userId: "Pessoa",
  startsAt: "Começa",
  endsAt: "Termina",
  dueAt: "Prazo",
  effectiveAt: "Vale em",
  amountCents: "Valor",
  requestedCents: "Pedido",
  status: "Situação",
  chargeId: "Cobrança",
  pixChargeId: "Cobrança do Pix",
  refundId: "Pedido de estorno",
  requestId: "Pedido",
  subscriptionId: "Assinatura",
  accountId: "Conta",
  resourceType: "Recurso",
  resourceId: "Código do recurso",
  label: "Nome",
  blockers: "Acima do plano",
  name: "Nome",
  manual: "Feito fora",
  approvalReason: "Motivo da aprovação",
  previousTerms: "Contrato anterior",
  from: "Veio da Empresa",
  courtesies: "Cortesias herdadas",
  origin: "Origem",
  signupPlan: "Plano escolhido no site",
  exceptions: "Exceções",
  overdueSince: "Vencida em",
  suspendedAt: "Suspensa em",
  suspensionScope: "O que parou",
  suspensionReason: "Motivo da suspensão",
  suspensionGraceUntil: "Liberada até",
  // A escolha do que fica: o que sai (os escolhidos e, ao concluir, quantos saíram).
  accounts: "Contas que saem",
  whatsapps: "Números que saem",
  crmBoards: "Quadros que saem",
  users: "Pessoas que saem",
  internalChatOff: "Sem o Chat Interno"
};
const KIND_LABELS = { limit: "Limite", courtesy: "Cortesia", price: "Preço negociado", discount: "Desconto" };
const RESOURCE_TYPES = { account: "Conta", whatsapp: "Número", crm_board: "Quadro do CRM", user: "Pessoa" };
const SUSPENSION_SCOPES = { all: "As mensagens e o Chat Interno", internal_chat: "O Chat Interno" };
const SUSPENSION_REASONS = { overdue: "Atraso no pagamento" };
const ORIGIN_LABELS = { signup: "Cadastro" };
const CHOICE_FIELDS = ["accounts", "whatsapps", "crmBoards", "users", "internalChatOff"];
// Vazio, estes campos dizem algo (nos outros, "-").
const EMPTY_TEXT = { plan: "Sem plano", endsAt: "Sem fim", aiPackMessages: "Sem pacote", suspendedAt: "Não suspensa" };
// Os dias: os das exceções e dos prazos, pelo dia de São Paulo; o vencimento, pelo dia da Pagar.me (0h UTC).
const DAY_FIELDS = ["startsAt", "endsAt", "dueAt", "effectiveAt"];
const gatewayDay = (value) => new Date(value).toLocaleDateString("pt-BR", { timeZone: "UTC" });
const resourceName = (key) => (RESOURCES.find((r) => r.key === key) || { label: key }).label;
const blockersText = (blockers) => blockers.map((b) => `${resourceName(b.resource)}: usa ${number(b.used)}, o plano permite ${number(b.limit)}`).join("; ");
/** O item de uma exceção pelo tipo dela (o "all" da cortesia é tudo; o do desconto, a mensalidade). */
const exceptionItemName = (kind, item) => {
  const list = kind === "limit" ? LIMIT_ITEMS : kind === "courtesy" ? COURTESY_ITEMS : DISCOUNT_ITEMS;
  const found = list.find((i) => i.key === item);
  return found ? found.label : item;
};
/** O valor de uma exceção pelo tipo dela: o limite (vazio é sem limite), o preço por unidade, o desconto. */
const exceptionValue = (kind, value) => {
  if (kind === "limit") return limitText(value);
  if (value === null || value === undefined) return "-";
  if (kind === "price") return money(value);
  if (kind === "discount") return `${value}%`;
  return number(value);
};
/** Um campo da trilha legível; `context` é o objeto em que ele está (o tipo da exceção dá o sentido do valor). */
export const fieldValue = (key, value, context = {}, people = []) => {
  if (key === "value" && context.kind) return exceptionValue(context.kind, value);
  if (value === null || value === undefined || value === "") return EMPTY_TEXT[key] || "-";
  if (typeof value === "boolean") return value ? "sim" : "não";
  if (key === "userId") {
    const person = people.find((p) => p.id === value);
    return person ? person.name : `#${value}`;
  }
  // Os códigos como estão (sem o ponto do milhar).
  if (/Id$/.test(key)) return `#${value}`;
  if (CHOICE_FIELDS.includes(key)) return number(Array.isArray(value) ? value.length : value);
  if (typeof value === "number") {
    if (/Cents$/.test(key)) return money(value);
    if (key === "aiPackMessages") return `${number(value)} mensagens`;
    return number(value);
  }
  if (typeof value === "string") {
    if (key === "overdueSince") return gatewayDay(value);
    if (DAY_FIELDS.includes(key)) return date(value);
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return dateTime(value);
    if (key === "plan" || key === "signupPlan") return PLAN_LABELS[value] || value;
    if (key === "enforcementMode") return MODE_LABELS[value] || value;
    if (key === "kind") {
      const refundKind = context.refundId ? REFUND_KIND[value] : null;
      return refundKind ? `${refundKind.charAt(0).toUpperCase()}${refundKind.slice(1)}` : KIND_LABELS[value] || value;
    }
    if (key === "item") return exceptionItemName(context.kind, value);
    // O preço combinado vale para um plano ou para um tamanho de pacote de IA.
    if (key === "appliesTo") return PLAN_LABELS[value] || (/^\d+$/.test(value) ? `${number(value)} mensagens` : value);
    if (key === "status") return DISPUTE_LABELS[value] || REFUND_STATUS[value] || value;
    if (key === "resourceType") return RESOURCE_TYPES[value] || value;
    if (key === "suspensionScope") return SUSPENSION_SCOPES[value] || value;
    if (key === "suspensionReason") return SUSPENSION_REASONS[value] || value;
    if (key === "origin") return ORIGIN_LABELS[value] || value;
    return value;
  }
  if (Array.isArray(value)) {
    if (key === "blockers") return blockersText(value);
    if (key === "exceptions") return value.length ? value.map(itemLabel).join("; ") : "Nenhuma";
    return `${value.length} ${value.length === 1 ? "item" : "itens"}`;
  }
  if (key === "from" && value.name) return value.name;
  // Um objeto (o contrato de antes, por exemplo): os campos dele, um nível.
  return Object.entries(value)
    .filter(([, inner]) => inner === null || typeof inner !== "object")
    .map(([innerKey, inner]) => `${FIELD_LABELS[innerKey] || innerKey}: ${fieldValue(innerKey, inner, value, people)}`)
    .join(", ");
};
/** O campo que só um dos lados guarda diz algo? (Os vazios da exceção criada e o que não saiu na escolha, não.) */
const saysSomething = (key, value, context) => {
  if (CHOICE_FIELDS.includes(key)) return (Array.isArray(value) ? value.length : Number(value)) > 0;
  if (value === null || value === undefined || value === "") return key in EMPTY_TEXT || (key === "value" && context.kind === "limit");
  return !(Array.isArray(value) && value.length === 0);
};
/**
 * As linhas do "antes e depois": o que mudou e o que só um dos lados guarda (a
 * trilha nem sempre grava os mesmos campos nos dois). O lado que não guarda
 * fica em branco: não registrado, e não apagado.
 */
export const fieldRows = (entry, people = []) => {
  const before = entry.before || {};
  const after = entry.after || {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((key) => key !== "id");
  return keys
    .filter((key) => {
      if (key in before && key in after) return JSON.stringify(before[key]) !== JSON.stringify(after[key]);
      return key in before ? saysSomething(key, before[key], before) : saysSomething(key, after[key], after);
    })
    .map((key) => ({
      key,
      label: FIELD_LABELS[key] || key,
      before: key in before ? fieldValue(key, before[key], before, people) : "",
      after: key in after ? fieldValue(key, after[key], after, people) : ""
    }));
};
// O impacto da trilha é a diferença na receita (com o sinal); nestas entradas, é o valor envolvido (sem sinal).
const AMOUNT_LABELS = {
  "billing.refund_requested": "Valor pedido",
  "billing.dispute_opened": "Valor contestado",
  "billing.dispute_lost": "Valor contestado",
  "billing.suspended": "Em atraso",
  "billing.double_payment": "Pago em dobro"
};
export const impactText = (entry) => {
  const cents = entry.financialImpactCents;
  if (AMOUNT_LABELS[entry.action]) return `${AMOUNT_LABELS[entry.action]}: ${money(Math.abs(cents))}`;
  return `Impacto: ${cents > 0 ? "+" : "-"}${money(Math.abs(cents))}`;
};
const HISTORY_TOPICS = [
  { key: "all", label: "Tudo" },
  { key: "plan", label: "Plano" },
  { key: "exceptions", label: "Exceções" },
  { key: "billing", label: "Cobrança" }
];

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#9CA3AF" : "#5b6474";
  const faint = dark ? "#71717A" : "#8a93a3";
  const divider = dark ? "rgba(255,255,255,0.06)" : "#e5e7eb";
  const hover = dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)";
  const focus = `1px solid ${dark ? "rgba(91,124,255,0.55)" : "rgba(61,92,253,0.5)"}`;
  const reducedMotion = "@media (prefers-reduced-motion: reduce)";
  const still = { [reducedMotion]: { animation: "none" } };
  return {
    // O fundo e o cartão da tela de Empresas (classes do pai) entram na página
    // depois destes e venceriam empatados: "&&" repete a classe e garante a
    // largura, a altura e o celular deste modal.
    backdrop: {
      animation: "$fadeIn 160ms ease-out",
      ...still,
      "&&": { [theme.breakpoints.down("xs")]: { padding: 0 } },
    },
    card: {
      position: "relative",
      outline: "none",
      animation: "$dialogIn 180ms ease-out",
      ...still,
      "&&": {
        maxWidth: 1120,
        minHeight: "min(640px, calc(100vh - 32px))",
        maxHeight: "calc(100vh - 32px)",
        [theme.breakpoints.down("xs")]: { height: "100%", minHeight: 0, maxHeight: "none", borderRadius: 0, border: "none" },
      },
    },
    header: {
      display: "flex", alignItems: "flex-start", gap: 12, padding: "22px 22px 18px 34px",
      borderBottom: `1px solid ${divider}`,
      [theme.breakpoints.down("xs")]: { padding: "14px 12px 12px 18px" },
    },
    headerText: { flex: 1, minWidth: 0 },
    headerSub: { fontSize: 13, color: muted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    tools: { display: "flex", alignItems: "center", gap: 2, marginTop: -2 },
    tool: {
      width: 36, height: 36, border: "none", background: "transparent", borderRadius: 10, cursor: "pointer", color: muted,
      display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "background 150ms, color 150ms",
      "&:hover": { background: hover, color: text },
      "&:focus-visible": { outline: focus },
    },
    toolActive: { background: hover, color: text },
    body: {
      flex: 1, minHeight: 0, overflowY: "auto", padding: "30px 34px 40px",
      [theme.breakpoints.down("xs")]: { padding: "20px 18px 28px" },
    },
    view: { animation: "$viewIn 170ms ease-out", ...still },
    footer: {
      display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 34px 20px", borderTop: `1px solid ${divider}`,
      [theme.breakpoints.down("xs")]: { padding: "12px 18px 16px" },
    },
    loading: { padding: "40px 0", textAlign: "center", fontSize: 13, color: muted },

    // O acordo
    deal: {
      display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 28, alignItems: "start",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    planName: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, fontSize: 26, fontWeight: 700, color: text, letterSpacing: "-0.5px", lineHeight: 1.2 },
    badge: {
      fontSize: 12, fontWeight: 600, letterSpacing: 0, padding: "3px 10px", borderRadius: 999, color: muted,
      border: `1px solid ${divider}`, background: dark ? "rgba(255,255,255,0.03)" : "#f8fafc",
    },
    contract: { fontSize: 14, color: muted, marginTop: 10, lineHeight: 1.55, maxWidth: 680 },
    mode: { display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 13, color: muted, "& strong": { color: text, fontWeight: 600 } },
    info: {
      display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: 5,
      color: faint, cursor: "help", outline: "none",
      "&:hover, &:focus-visible": { color: muted, background: hover },
    },

    section: { marginTop: 34, paddingTop: 30, borderTop: `1px solid ${divider}` },
    sectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 22, minHeight: 36 },
    sectionTitle: { fontSize: 14, fontWeight: 650, color: text },

    // Uso
    usage: {
      display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "28px 40px",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    },
    usageLabel: { display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 500, color: muted },
    usageValue: { display: "flex", alignItems: "baseline", gap: 7, marginTop: 10, fontVariantNumeric: "tabular-nums" },
    usageUsed: { fontSize: 22, fontWeight: 700, color: text, letterSpacing: "-0.3px", lineHeight: 1 },
    usageOf: { fontSize: 12.5, fontWeight: 500, color: faint },
    usageOver: { color: dark ? "#fca5a5" : "#b91c1c" },
    bar: { marginTop: 12 },
    renews: { fontSize: 13, color: muted, fontVariantNumeric: "tabular-nums" },

    // Exceções
    list: { listStyle: "none", margin: 0, padding: 0 },
    item: {
      display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto minmax(150px, auto) auto", alignItems: "center", gap: 24,
      padding: "16px 0",
      "& + &": { borderTop: `1px solid ${divider}` },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr) auto", rowGap: 6 },
    },
    itemTitle: { fontSize: 13.5, fontWeight: 600, color: text },
    itemReason: { fontSize: 12.5, color: muted, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    itemMeta: { fontSize: 12.5, color: muted, fontVariantNumeric: "tabular-nums" },
    itemEnded: { "& $itemTitle, & $itemMeta": { color: faint } },
    pill: { fontSize: 11.5, fontWeight: 600, padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap", justifySelf: "start" },
    pillActive: { color: dark ? "#86efac" : "#15803d", background: dark ? "rgba(34,197,94,0.10)" : "rgba(21,128,61,0.08)" },
    pillScheduled: { color: dark ? "#fcd34d" : "#a16207", background: dark ? "rgba(245,158,11,0.10)" : "rgba(161,98,7,0.08)" },
    pillEnded: { color: faint, background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" },
    textBtn: {
      border: "none", background: "transparent", padding: "5px 9px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
      fontSize: 12.5, fontWeight: 600, color: muted, display: "inline-flex", alignItems: "center", gap: 6,
      "&:hover": { color: text, background: hover },
      "&:focus-visible": { outline: focus },
      "& svg": { transition: "transform 160ms ease" },
      [reducedMotion]: { "& svg": { transition: "none" } },
    },
    textBtnOpen: { "& svg": { transform: "rotate(180deg)" } },
    endedToggle: { marginTop: 8, marginLeft: -9 },
    empty: { fontSize: 13, color: muted, padding: "4px 0 2px" },
    revoke: {
      gridColumn: "1 / -1", display: "grid", gap: 4, padding: "14px 16px 12px", marginTop: 4, borderRadius: 12,
      background: dark ? "rgba(255,255,255,0.03)" : "#f6f7f9",
      animation: "$reveal 150ms ease-out", ...still,
    },
    revokeActions: { display: "flex", justifyContent: "flex-end", gap: 8 },

    // Formulários
    viewHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 26, marginLeft: -8 },
    viewTitle: { fontSize: 17, fontWeight: 650, color: text, letterSpacing: "-0.2px" },
    formGrid: {
      display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)", gap: 44, alignItems: "start",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr", gap: 8 },
    },
    // O select com a cara dos campos de texto do modal (fieldInput, da tela de Empresas).
    selectWrap: { minWidth: 0 },
    selectInput: {
      "&& .MuiOutlinedInput-root, && .MuiOutlinedInput-root:hover:not(.Mui-disabled), && .MuiOutlinedInput-root.Mui-focused": {
        height: 44, borderRadius: 14, fontSize: 14, background: dark ? "rgba(255,255,255,0.05)" : "#ffffff",
      },
      "&& .MuiSelect-select": { height: 44, minHeight: 0, boxSizing: "border-box", padding: "0 34px 0 14px", lineHeight: "44px" },
      "&& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: dark ? "rgba(91,124,255,0.15)" : "rgba(15,23,42,0.12)" },
      "&& .MuiOutlinedInput-root:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": { borderColor: dark ? "rgba(91,124,255,0.28)" : "rgba(61,92,253,0.22)" },
      "&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: dark ? "rgba(91,124,255,0.45)" : "rgba(61,92,253,0.40)" },
      "&& .MuiOutlinedInput-root.Mui-focused": { boxShadow: dark ? "0 0 0 1px rgba(91,124,255,0.15)" : "0 0 0 1px rgba(61,92,253,0.12)" },
    },
    textarea: { height: "auto !important", minHeight: 80, padding: "10px 14px !important", resize: "vertical", lineHeight: 1.45 },
    // O navegador recua a data um pouco: o texto começa na mesma linha dos outros campos.
    dateInput: { colorScheme: dark ? "dark" : "light", "&::-webkit-datetime-edit": { marginLeft: -2 } },
    staticValue: {
      display: "flex", alignItems: "center", height: 44, padding: "0 14px", boxSizing: "border-box", borderRadius: 14,
      fontSize: 14, color: text, background: dark ? "rgba(255,255,255,0.03)" : "#f6f7f9",
    },
    effect: {
      position: "sticky", top: 0, padding: "20px 22px", borderRadius: 14,
      background: dark ? "rgba(255,255,255,0.03)" : "#f6f7f9",
    },
    effectTitle: { fontSize: 12.5, fontWeight: 600, color: muted, marginBottom: 14 },
    effectGrid: {
      display: "grid", gridTemplateColumns: "1fr auto", rowGap: 11, columnGap: 20, fontSize: 13, color: text, fontVariantNumeric: "tabular-nums",
      "& > span:nth-child(odd)": { color: muted },
      "& > span:nth-child(even)": { textAlign: "right" },
    },
    effectWarn: { color: `${dark ? "#fcd34d" : "#a16207"} !important`, fontWeight: 600 },
    effectNote: { fontSize: 12.5, color: muted, marginTop: 14, lineHeight: 1.5 },
    warnNote: { fontSize: 12.5, color: dark ? "#fcd34d" : "#a16207", marginTop: 14, lineHeight: 1.5 },

    // Histórico
    veil: {
      position: "absolute", inset: 0, background: dark ? "rgba(0,0,0,0.45)" : "rgba(15,23,42,0.16)",
      animation: "$fadeIn 160ms ease-out", ...still,
    },
    history: {
      position: "absolute", top: 0, right: 0, bottom: 0, width: 400, maxWidth: "100%", display: "flex", flexDirection: "column",
      background: dark ? "#141416" : "#ffffff", borderLeft: `1px solid ${divider}`,
      boxShadow: dark ? "-16px 0 40px rgba(0,0,0,0.45)" : "-16px 0 40px rgba(15,23,42,0.10)",
      animation: "$panelIn 200ms ease-out", ...still,
    },
    historyHead: {
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "16px 12px 14px 22px",
      borderBottom: `1px solid ${divider}`,
    },
    historyTitle: { fontSize: 15, fontWeight: 650, color: text },
    historyList: { flex: 1, overflowY: "auto", listStyle: "none", margin: 0, padding: "4px 22px 22px" },
    historyItem: {
      padding: "14px 0", borderRadius: 2, "& + &": { borderTop: `1px solid ${divider}` },
      "&:focus": { outline: "none" },
      "&:focus-visible": { outline: focus, outlineOffset: 2 },
    },
    historyWhen: { fontSize: 12, color: faint, fontVariantNumeric: "tabular-nums" },
    historyWhat: { fontSize: 13.5, fontWeight: 600, color: text, marginTop: 4 },
    historyActor: { fontSize: 12.5, fontWeight: 500, color: muted },
    historyDetail: { fontSize: 12.5, color: muted, marginTop: 3, lineHeight: 1.45 },
    historyReason: { fontSize: 12.5, color: text, marginTop: 6, lineHeight: 1.45 },
    historyMeta: { display: "flex", flexWrap: "wrap", gap: "2px 12px", fontSize: 12.5, color: muted, marginTop: 3, fontVariantNumeric: "tabular-nums" },
    historyFilters: { display: "flex", gap: 6, padding: "12px 22px", borderBottom: `1px solid ${divider}`, flexWrap: "wrap" },
    historyFilter: {
      height: 30, padding: "0 12px", borderRadius: 8, border: `1px solid ${divider}`, background: "transparent", color: muted,
      fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, cursor: "pointer", transition: "background 150ms, color 150ms",
      "&:hover": { background: hover, color: text },
      "&:focus-visible": { outline: focus, outlineOffset: 1 },
      "&[aria-disabled='true']": { cursor: "default", opacity: 0.6 },
    },
    historyFilterOn: { background: dark ? "#FAFAFA" : "#09090B", borderColor: dark ? "#FAFAFA" : "#09090B", color: dark ? "#09090B" : "#ffffff", "&:hover": { background: dark ? "#FAFAFA" : "#09090B", color: dark ? "#09090B" : "#ffffff" } },
    historyToggle: {
      marginTop: 6, padding: 0, border: "none", background: "transparent", color: muted, fontFamily: "inherit", fontSize: 12.5, fontWeight: 600,
      cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, borderRadius: 4,
      "&:hover": { color: text },
      "&:focus-visible": { outline: focus, outlineOffset: 2 },
    },
    historyFields: {
      width: "100%", marginTop: 8, borderCollapse: "collapse", fontSize: 12, color: text, tableLayout: "fixed",
      "& th, & td": { textAlign: "left", padding: "5px 6px 5px 0", verticalAlign: "top", borderTop: `1px solid ${divider}`, overflowWrap: "anywhere" },
      "& thead th": { borderTop: "none", color: faint, fontWeight: 600, fontSize: 11.5 },
      "& tbody th": { color: muted, fontWeight: 600, width: "30%" },
      "& caption": { captionSide: "bottom", textAlign: "left", paddingTop: 6, color: faint, fontSize: 11.5 },
    },
    historyMoreRow: { padding: "14px 0 0", borderTop: `1px solid ${divider}`, textAlign: "center" },
    historyMore: {
      height: 34, padding: "0 16px", borderRadius: 9, border: `1px solid ${divider}`, background: "transparent", color: text,
      fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer",
      "&:hover": { background: hover },
      "&:focus-visible": { outline: focus, outlineOffset: 1 },
      "&[aria-disabled='true']": { cursor: "default", opacity: 0.6 },
    },
    srOnly: { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", border: 0 },

    "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
    "@keyframes dialogIn": { from: { opacity: 0, transform: "translateY(8px) scale(0.985)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes viewIn": { from: { opacity: 0, transform: "translateX(10px)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes reveal": { from: { opacity: 0, transform: "translateY(-4px)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes panelIn": { from: { opacity: 0, transform: "translateX(24px)" }, to: { opacity: 1, transform: "none" } },
  };
});

const InfoTip = ({ s, title, label }) => (
  <Tooltip title={title} arrow placement="top">
    <span className={s.info} tabIndex={0} aria-label={label} role="img">
      <Info size={13} strokeWidth={2} />
    </span>
  </Tooltip>
);

// O select do modal: o rótulo em cima e a dica embaixo, como os outros campos.
const SelectField = ({ classes, s, id, label, hint, children, ...props }) => (
  <div className={`${classes.field} ${s.selectWrap}`}>
    <div id={`${id}-label`} className={classes.fieldLabel}>{label}</div>
    <ModernSelect
      id={id}
      labelId={`${id}-label`}
      fullWidth
      className={s.selectInput}
      SelectDisplayProps={hint ? { "aria-describedby": `${id}-hint` } : undefined}
      {...props}
    >
      {children}
    </ModernSelect>
    {hint && <div id={`${id}-hint`} className={classes.fieldHint}>{hint}</div>}
  </div>
);

const ReasonField = ({ classes, s, value, onChange, placeholder, label = "Motivo", id = "commercial-reason", inputRef }) => (
  <div className={classes.field}>
    <label className={classes.fieldLabel} htmlFor={id}>{label}</label>
    <textarea
      id={id}
      ref={inputRef}
      className={`${classes.fieldInput} ${s.textarea}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={1000}
    />
    <div className={classes.fieldHint}>Fica no histórico da Empresa.</div>
  </div>
);

/** Uma contestação no cartão: o valor, quando, o estado e, aberta, o resultado na própria linha (com motivo). */
const DisputeRow = ({ classes, s, summary, dispute, busy, setBusy, onSaved }) => {
  const [outcome, setOutcome] = useState(null);
  const [reason, setReason] = useState("");
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/company-groups/${summary.group.id}/disputes/${dispute.chargeId}/resolve`, { outcome, reason });
      toast.success(outcome === "won" ? "Contestação marcada como ganha" : "Contestação marcada como perdida: a cobrança é devida de novo");
      setOutcome(null);
      setReason("");
      onSaved(data);
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };
  return (
    <li className={s.item}>
      <div style={{ minWidth: 0 }}>
        <div className={s.itemTitle}>{`Cobrança de ${money(dispute.amountCents)} contestada em ${dispute.disputedAt ? date(dispute.disputedAt) : "-"}`}</div>
        <div className={s.itemReason}>{DISPUTE_LABELS[dispute.status] || dispute.status}</div>
      </div>
      {dispute.status === "open" && !outcome && (
        <div className={s.revokeActions}>
          <button className={s.textBtn} onClick={() => setOutcome("won")} disabled={busy}>Ganha</button>
          <button className={s.textBtn} onClick={() => setOutcome("lost")} disabled={busy}>Perdida</button>
        </div>
      )}
      {/* A contestação revertida pela operadora, ou o resultado marcado por engano: corrige, com motivo. */}
      {dispute.status !== "open" && !outcome && (
        <button className={s.textBtn} onClick={() => setOutcome(dispute.status === "won" ? "lost" : "won")} disabled={busy}>
          {dispute.status === "won" ? "Corrigir para perdida" : "Corrigir para ganha"}
        </button>
      )}
      {outcome && (
        <div className={s.revoke} role="group" aria-label={outcome === "won" ? "Marcar como ganha" : "Marcar como perdida"}>
          <ReasonField
            classes={classes}
            s={s}
            id={`dispute-reason-${dispute.chargeId}`}
            value={reason}
            onChange={setReason}
            label={outcome === "won" ? "Motivo (ganha: o valor fica com a Sttelar)" : "Motivo (perdida: a cobrança é devida de novo, com 7 dias)"}
            placeholder="Ex.: resposta da operadora em 12/11"
          />
          <div className={s.revokeActions}>
            <button className={classes.ghostBtn} onClick={() => setOutcome(null)} disabled={busy}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={save} disabled={busy || reason.trim().length < 3}>
              {busy ? "Salvando..." : outcome === "won" ? "Marcar como ganha" : "Marcar como perdida"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

/**
 * Um pedido de estorno: o valor, quando, o motivo de quem pediu e o estado; a
 * decisão na própria linha (recusar, aprovar como correção ou anulação, com
 * motivo e o valor). Aprovado: o "feito fora" (o boleto, o Pix em parte, ou o
 * que a Pagar.me não fez), o "tentar de novo" (relê antes de pedir) e o
 * "desfazer aprovação"; devolvendo: o "conferir".
 */
const RefundRow = ({ classes, s, summary, refund, busy, setBusy, onSaved, onReload }) => {
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState(String((refund.amountCents / 100).toFixed(2)).replace(".", ","));
  const call = async (path, body, done) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/company-groups/${summary.group.id}/refunds/${refund.id}/${path}`, body);
      toast.success(done);
      setAction(null);
      setReason("");
      onSaved(data);
    } catch (err) {
      toastError(err);
      setAction(null);
      setBusy(false);
      // A aprovação que a Pagar.me recusou fica aprovada: a linha relê o estado (o "tentar de novo" aparece).
      onReload();
    }
  };
  const cents = Math.round(Number(amount.replace(/\./g, "").replace(",", ".")) * 100);
  const decide = () => {
    if (action === "done") return call("done", { reason }, "Estorno marcado como feito");
    if (action === "cancel") return call("cancel", { reason }, "Aprovação desfeita");
    return call("decide", { decision: action, reason, ...(action === "refuse" ? {} : { amountCents: cents }) }, action === "refuse" ? "Estorno recusado" : "Estorno aprovado");
  };
  const approving = action === "correction" || action === "annulment";
  const label =
    refund.status === "refunded" && refund.kind
      ? `${REFUND_STATUS.refunded} (${REFUND_KIND[refund.kind]}${refund.manual ? ", feito fora" : ""})`
      : refund.status === "approved" && refund.manual
        ? "Aprovado: devolva fora da Pagar.me e marque"
        : REFUND_STATUS[refund.status] || refund.status;
  const gatewayFailed = refund.status === "approved" && !refund.manual;
  return (
    <li className={s.item}>
      <div style={{ minWidth: 0 }}>
        <div className={s.itemTitle}>{`Estorno de ${money(refund.amountCents)} pedido em ${date(refund.createdAt)}`}</div>
        <div className={s.itemReason} title={refund.reason}>{`${label}. "${refund.reason}"`}</div>
        {/* O motivo inteiro, sem cortar: é ele que diz ao super o que fazer. */}
        {gatewayFailed && refund.failureReason && <div className={s.itemReason} style={{ whiteSpace: "normal" }}>{`A Pagar.me não estornou: ${refund.failureReason}`}</div>}
        {refund.status === "refunded" && refund.failureReason && (
          <div className={s.itemReason} style={{ whiteSpace: "normal" }}>{`A anulação não terminou (${refund.failureReason}): ajuste o plano.`}</div>
        )}
      </div>
      {!action && refund.status === "requested" && (
        <div className={s.revokeActions}>
          <button className={s.textBtn} onClick={() => setAction("refuse")} disabled={busy}>Recusar</button>
          <button className={s.textBtn} onClick={() => setAction("correction")} disabled={busy}>Aprovar: correção</button>
          <button className={s.textBtn} onClick={() => setAction("annulment")} disabled={busy}>Aprovar: anulação</button>
        </div>
      )}
      {!action && refund.status === "approved" && (
        <div className={s.revokeActions}>
          {gatewayFailed && <button className={s.textBtn} onClick={() => call("retry", {}, "Estorno enviado à Pagar.me")} disabled={busy}>Tentar de novo</button>}
          <button className={s.textBtn} onClick={() => setAction("done")} disabled={busy}>Marcar como feito fora</button>
          <button className={s.textBtn} onClick={() => setAction("cancel")} disabled={busy}>Desfazer aprovação</button>
        </div>
      )}
      {!action && refund.status === "refunding" && (
        <button className={s.textBtn} onClick={() => call("retry", {}, "Estorno conferido na Pagar.me")} disabled={busy}>Conferir</button>
      )}
      {action && (
        <div className={s.revoke} role="group" aria-label="Decidir o estorno">
          {approving && (
            <div className={classes.field}>
              <label className={classes.fieldLabel} htmlFor={`refund-amount-${refund.id}`}>Valor a devolver (R$)</label>
              <input id={`refund-amount-${refund.id}`} className={classes.fieldInput} value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
              <div className={classes.fieldHint}>
                {action === "correction"
                  ? "Correção: o plano não muda (a cobrança em dobro, o valor errado)."
                  : "Anulação: desfaz o que a cobrança contratou (a assinatura: o Starter em 7 dias, com a escolha do que fica)."}
                {refund.paymentMethod === "boleto"
                  ? " O boleto é devolvido fora da Pagar.me: depois, marque como feito."
                  : refund.paymentMethod === "pix"
                    ? " No Pix, a Pagar.me só devolve o valor inteiro; em parte, devolva fora e marque como feito."
                    : " No cartão, o estorno sai na hora."}
              </div>
            </div>
          )}
          <ReasonField
            classes={classes}
            s={s}
            id={`refund-reason-${refund.id}`}
            value={reason}
            onChange={setReason}
            label={action === "refuse" ? "Motivo da recusa (a Empresa vê)" : action === "cancel" ? "Por que desfazer (a Empresa vê)" : action === "done" ? "Como foi devolvido" : "Motivo"}
            placeholder={action === "done" ? "Ex.: transferência Pix em 12/11" : action === "cancel" ? "Ex.: a Pagar.me não devolve Pix com mais de 90 dias" : "Ex.: cobrança em dobro confirmada"}
          />
          <div className={s.revokeActions}>
            <button className={classes.ghostBtn} onClick={() => setAction(null)} disabled={busy}>Cancelar</button>
            <button
              className={action === "refuse" || action === "cancel" ? classes.detailDeleteBtn : classes.primaryBtn}
              onClick={decide}
              disabled={busy || reason.trim().length < 3 || (approving && !(cents > 0 && cents <= refund.amountCents))}
            >
              {busy
                ? "Salvando..."
                : action === "refuse"
                  ? "Recusar estorno"
                  : action === "cancel"
                    ? "Desfazer aprovação"
                    : action === "done"
                      ? "Marcar como feito"
                      : "Aprovar e estornar"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

/** Os pedidos de estorno da Empresa (etapa 10): só o super decide e devolve. */
const RefundsSection = ({ classes, s, summary, busy, setBusy, onSaved, onReload }) => (
  <section className={s.section} aria-label="Estornos">
    <div className={s.sectionHead}>
      <span className={s.sectionTitle}>Estornos</span>
    </div>
    <ul className={s.list} aria-label="Pedidos de estorno">
      {summary.refunds.map((refund) => (
        <RefundRow key={refund.id} classes={classes} s={s} summary={summary} refund={refund} busy={busy} setBusy={setBusy} onSaved={onSaved} onReload={onReload} />
      ))}
    </ul>
  </section>
);

/**
 * O atraso no pagamento (etapa 10): desde quando, quando suspende ou desde
 * quando está suspensa, e "Retirar suspensão" na própria linha, com o motivo
 * (o atraso só suspende de novo depois de 7 dias).
 */
const DelinquencySection = ({ classes, s, summary, busy, setBusy, onSaved }) => {
  const { group } = summary;
  const disputes = summary.disputes || [];
  const [lifting, setLifting] = useState(false);
  const [reason, setReason] = useState("");
  const reasonRef = useRef(null);
  const liftButton = useRef(null);
  // Aberto, o foco vai para o motivo; fechado sem retirar, volta ao botão.
  const opened = useRef(false);
  useEffect(() => {
    if (lifting && reasonRef.current) reasonRef.current.focus();
    else if (!lifting && opened.current && liftButton.current) liftButton.current.focus();
    opened.current = lifting;
  }, [lifting]);
  const lift = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/company-groups/${group.id}/suspension/lift`, { reason });
      toast.success("Suspensão retirada");
      setLifting(false);
      setReason("");
      onSaved(data);
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };
  // Os vencimentos da Pagar.me são dias (meia-noite em UTC).
  const day = (value) => new Date(value).toLocaleDateString("pt-BR", { timeZone: "UTC" });
  let text;
  if (group.suspendedAt) text = `Suspensa desde ${dateTime(group.suspendedAt)}: ${SCOPE_LABELS[group.suspensionScope] || SCOPE_LABELS.all}. Ler, organizar e Faturamento seguem.`;
  else if (group.overdueSince && group.suspensionGraceUntil) text = `Fatura vencida em ${day(group.overdueSince)}. Suspensão retirada; o atraso só suspende de novo depois de ${dateTime(group.suspensionGraceUntil)}.`;
  else if (!group.overdueSince) text = null;
  else if (group.payBy) text = `Fatura vencida em ${day(group.overdueSince)}. Sem o pagamento até ${day(group.payBy)}, a Empresa é suspensa (o envio e o Chat Interno param).`;
  else text = `Fatura vencida em ${day(group.overdueSince)}. Sem suspensão anunciada (limites fora do bloqueio ou suspensões desligadas).`;
  return (
    <section className={s.section} aria-label="Pagamento">
      <div className={s.sectionHead}>
        <span className={s.sectionTitle}>Pagamento</span>
        {group.suspendedAt && !lifting && (
          <button ref={liftButton} className={classes.detailEditBtn} onClick={() => setLifting(true)} disabled={busy}>Retirar suspensão</button>
        )}
      </div>
      {text && <div className={s.contract}>{text}</div>}
      {lifting && (
        <div className={s.revoke} role="group" aria-label="Retirar suspensão">
          <ReasonField classes={classes} s={s} id="lift-suspension-reason" inputRef={reasonRef} value={reason} onChange={setReason} label="Motivo" placeholder="Ex.: pagamento combinado com o financeiro para 10/11" />
          <div className={s.revokeActions}>
            <button className={classes.ghostBtn} onClick={() => setLifting(false)} disabled={busy}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={lift} disabled={busy || reason.trim().length < 3}>
              {busy ? "Retirando..." : "Retirar suspensão por 7 dias"}
            </button>
          </div>
        </div>
      )}
      {disputes.length > 0 && (
        <ul className={s.list} aria-label="Contestações no cartão">
          {disputes.map((dispute) => (
            <DisputeRow key={dispute.chargeId} classes={classes} s={s} summary={summary} dispute={dispute} busy={busy} setBusy={setBusy} onSaved={onSaved} />
          ))}
        </ul>
      )}
    </section>
  );
};

/** Revogar na própria linha: o motivo e a confirmação (com a trava do modal: não fecha no meio). */
const RevokeInline = ({ classes, s, summary, exception, busy, setBusy, onCancel, onSaved }) => {
  const [reason, setReason] = useState("");
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/company-groups/${summary.group.id}/exceptions/${exception.id}/revoke`, { reason });
      toast.success("Exceção revogada");
      onSaved(data);
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };
  return (
    <div className={s.revoke} role="group" aria-label={`Revogar ${itemLabel(exception)}`}>
      <ReasonField classes={classes} s={s} value={reason} onChange={setReason} label="Motivo da revogação" placeholder="Ex.: migrou para o plano Basic" />
      <div className={s.revokeActions}>
        <button className={classes.ghostBtn} onClick={onCancel} disabled={busy}>Cancelar</button>
        <button className={classes.detailDeleteBtn} onClick={save} disabled={busy || reason.trim().length < 3}>
          {busy ? "Revogando..." : "Revogar exceção"}
        </button>
      </div>
    </div>
  );
};

const UsageItem = ({ classes, s, label, help, used, limit }) => {
  const { hasLimit } = meterState(used, limit);
  const isOver = hasLimit && used > limit;
  return (
    <div role="group" aria-label={hasLimit ? `${label}: ${used} de ${limit}` : `${label}: ${used}, sem limite`}>
      <div className={s.usageLabel}>
        {label}
        <InfoTip s={s} title={help} label={`Sobre ${label}`} />
      </div>
      <div className={s.usageValue}>
        <span className={`${s.usageUsed}${isOver ? ` ${s.usageOver}` : ""}`}>{number(used)}</span>
        <span className={s.usageOf}>{hasLimit ? `de ${number(limit)}` : "sem limite"}</span>
      </div>
      {hasLimit && <MeterBar classes={classes} used={used} limit={limit} small className={s.bar} />}
    </div>
  );
};

const SummaryView = ({ classes, s, summary, busy, setBusy, onEditPlan, onGrant, onSaved, onReload }) => {
  const [showEnded, setShowEnded] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const { group, limits, usage, messageUsage } = summary;
  const current = summary.exceptions.filter((e) => !isEnded(e));
  const ended = summary.exceptions.filter(isEnded);
  const pillClass = { active: s.pillActive, scheduled: s.pillScheduled, expired: s.pillEnded, revoked: s.pillEnded };

  const row = (exception) => (
    <li key={exception.id} className={`${s.item}${isEnded(exception) ? ` ${s.itemEnded}` : ""}`}>
      <div style={{ minWidth: 0 }}>
        <div className={s.itemTitle}>{itemLabel(exception)}</div>
        <div className={s.itemReason} title={exception.status === "revoked" ? exception.revokeReason : exception.reason}>
          {exception.status === "revoked" ? `Revogada: ${exception.revokeReason}` : exception.reason}
        </div>
      </div>
      <span className={`${s.pill} ${pillClass[exception.status]}`}>{STATUS_LABELS[exception.status]}</span>
      <span className={s.itemMeta}>{validity(exception)}</span>
      {isEnded(exception) || revokingId === exception.id ? <span /> : (
        <button className={s.textBtn} onClick={() => setRevokingId(exception.id)} aria-label={`Revogar ${itemLabel(exception)}`}>
          Revogar
        </button>
      )}
      {revokingId === exception.id && (
        <RevokeInline
          classes={classes}
          s={s}
          summary={summary}
          exception={exception}
          busy={busy}
          setBusy={setBusy}
          onCancel={() => setRevokingId(null)}
          onSaved={(data) => { setRevokingId(null); onSaved(data); }}
        />
      )}
    </li>
  );

  return (
    <div className={s.view}>
      <div className={s.deal}>
        <div>
          <div className={s.planName}>
            {group.plan ? PLAN_LABELS[group.plan] : "Sem plano"}
            {summary.courtesyAll && <span className={s.badge}>Cortesia total</span>}
          </div>
          <div className={s.contract}>{contractText(group, summary.catalog.plans)}</div>
          {group.plan && (
            <div className={s.mode}>
              Limites do plano: <strong>{MODE_LABELS[group.enforcementMode]}</strong>
              <InfoTip s={s} title={MODE_HELP[group.enforcementMode]} label="Sobre os limites do plano" />
            </div>
          )}
        </div>
        <button className={group.plan ? classes.detailEditBtn : classes.primaryBtn} onClick={onEditPlan} disabled={busy}>
          {group.plan ? "Alterar plano" : "Escolher plano"}
        </button>
      </div>

      {(group.overdueSince || group.suspendedAt || (summary.disputes || []).length > 0) && (
        <DelinquencySection classes={classes} s={s} summary={summary} busy={busy} setBusy={setBusy} onSaved={onSaved} />
      )}

      {(summary.refunds || []).length > 0 && <RefundsSection classes={classes} s={s} summary={summary} busy={busy} setBusy={setBusy} onSaved={onSaved} onReload={onReload} />}

      <section className={s.section} aria-label="Uso agora">
        <div className={s.sectionHead}><span className={s.sectionTitle}>Uso agora</span></div>
        <div className={s.usage}>
          {RESOURCES.map(({ key, label, help }) => (
            <UsageItem key={key} classes={classes} s={s} label={label} help={help} used={usage[key]} limit={limits[key]} />
          ))}
        </div>
      </section>

      {messageUsage && (
        <section className={s.section} aria-label="Mensagens do mês">
          <div className={s.sectionHead}>
            <span className={s.sectionTitle}>Mensagens do mês</span>
            <span className={s.renews}>Renova em {date(messageUsage.endsAt)}</span>
          </div>
          <div className={s.usage}>
            {MESSAGE_USAGE.map(({ key, label, help }) => (
              <UsageItem key={key} classes={classes} s={s} label={label} help={help} used={messageUsage[key].used} limit={messageUsage[key].limit} />
            ))}
          </div>
        </section>
      )}

      <section className={s.section} aria-label="Exceções e cortesias">
        <div className={s.sectionHead}>
          <span className={s.sectionTitle}>Exceções e cortesias</span>
          <button className={classes.detailEditBtn} onClick={onGrant} disabled={busy}>Conceder exceção</button>
        </div>
        {current.length === 0 && <div className={s.empty}>Nenhuma exceção valendo. Valem os limites do plano.</div>}
        {current.length > 0 && <ul className={s.list}>{current.map(row)}</ul>}
        {ended.length > 0 && (
          <>
            <button
              className={`${s.textBtn} ${s.endedToggle}${showEnded ? ` ${s.textBtnOpen}` : ""}`}
              onClick={() => setShowEnded((open) => !open)}
              aria-expanded={showEnded}
            >
              {showEnded ? "Esconder" : "Ver"} encerradas e revogadas ({ended.length})
              <ChevronDown size={14} />
            </button>
            {showEnded && <ul className={`${s.list} ${s.view}`}>{ended.map(row)}</ul>}
          </>
        )}
      </section>
    </div>
  );
};

const PlanView = ({ classes, s, summary, form, set }) => {
  const planKey = form.plan !== NONE ? form.plan : null;
  const plan = planKey ? summary.catalog.plans[planKey] : null;
  const limits = useMemo(() => previewLimits(form, summary), [form, summary]);
  const over = RESOURCES.filter(({ key }) => limits[key] != null && summary.usage[key] > limits[key]);
  const isResource = (key) => RESOURCES.some((r) => r.key === key);

  return (
    <div className={s.formGrid}>
      <div>
        <SelectField
          classes={classes}
          s={s}
          id="plan-choice"
          label="Plano"
          value={form.plan}
          onChange={(e) => set({ plan: e.target.value, enforcementMode: e.target.value !== NONE ? form.enforcementMode : "off", aiPackMessages: NONE })}
          autoFocus
        >
          <MenuItem value={NONE}>Sem plano</MenuItem>
          {Object.keys(PLAN_LABELS).map((key) => (
            <MenuItem key={key} value={key}>{PLAN_LABELS[key]}</MenuItem>
          ))}
        </SelectField>
        <SelectField
          classes={classes}
          s={s}
          id="plan-mode"
          label="Limites do plano"
          value={planKey ? form.enforcementMode : "off"}
          onChange={(e) => set({ enforcementMode: e.target.value })}
          disabled={!planKey}
          hint={MODE_HELP[planKey ? form.enforcementMode : "off"]}
        >
          {Object.keys(MODE_LABELS).map((key) => (
            <MenuItem key={key} value={key}>{MODE_LABELS[key]}</MenuItem>
          ))}
        </SelectField>

        {plan && (
          <div className={classes.fieldRow}>
            {plan.maxUsers == null ? (
              <div className={classes.field}>
                <label className={classes.fieldLabel} htmlFor="plan-seats">Licenças contratadas</label>
                <input
                  id="plan-seats"
                  type="number"
                  min={plan.minBilledUsers}
                  className={classes.fieldInput}
                  value={form.seatsContracted}
                  onChange={(e) => set({ seatsContracted: e.target.value })}
                  placeholder={String(plan.minBilledUsers)}
                />
                <div className={classes.fieldHint}>Mínimo de {plan.minBilledUsers}.</div>
              </div>
            ) : (
              <div className={classes.field}>
                <div className={classes.fieldLabel}>Pessoas</div>
                <div className={s.staticValue}>Até {plan.maxUsers}</div>
              </div>
            )}
            <div className={classes.field}>
              <label className={classes.fieldLabel} htmlFor="plan-internal">Licenças de Chat Interno</label>
              <input
                id="plan-internal"
                type="number"
                min={0}
                className={classes.fieldInput}
                value={form.internalChatSeats}
                onChange={(e) => set({ internalChatSeats: e.target.value })}
                placeholder="0"
              />
              <div className={classes.fieldHint}>{money(summary.catalog.internalChatPriceCents)} por pessoa ao mês.</div>
            </div>
          </div>
        )}

        {plan && plan.aiPacks && (
          <SelectField
            classes={classes}
            s={s}
            id="plan-ai-pack"
            label="Pacote de IA"
            value={form.aiPackMessages}
            onChange={(e) => set({ aiPackMessages: e.target.value })}
            hint="A franquia total do mês: o pacote substitui a do plano, não soma."
          >
            <MenuItem value={NONE}>Sem pacote ({number(plan.aiMessages)} mensagens do plano)</MenuItem>
            {summary.catalog.aiPacks.map((pack) => (
              <MenuItem key={pack.messages} value={String(pack.messages)}>
                {number(pack.messages)} mensagens ({money(pack.priceCents)} ao mês)
              </MenuItem>
            ))}
          </SelectField>
        )}

        <ReasonField classes={classes} s={s} value={form.reason} onChange={(reason) => set({ reason })} placeholder="Ex.: contrato assinado em 29/10" />
      </div>

      <aside className={s.effect} aria-live="polite" aria-label="Com essa escolha">
        <div className={s.effectTitle}>Com essa escolha</div>
        <div className={s.effectGrid}>
          {LIMIT_ITEMS.map(({ key, label }) => {
            const tooMuch = isResource(key) && limits[key] != null && summary.usage[key] > limits[key];
            return (
              <React.Fragment key={key}>
                <span>{label}</span>
                <span className={tooMuch ? s.effectWarn : undefined}>
                  {isResource(key) ? usedOfLimit(summary.usage[key], limits[key]) : limitText(limits[key]).toLowerCase()}
                </span>
              </React.Fragment>
            );
          })}
        </div>
        {over.length > 0 && planKey && form.enforcementMode !== "off" && (
          <div className={s.warnNote}>
            {over.map((r) => r.label).join(", ")} acima do limite. Nada é apagado, mas
            {form.enforcementMode === "enforce" ? " novas criações serão recusadas." : " fica registrado quem passaria do limite."}
          </div>
        )}
      </aside>
    </div>
  );
};

const GrantView = ({ classes, s, summary, people, form, set }) => {
  const resource = LIMIT_ITEMS.find((i) => i.key === form.item);
  const courtesy = COURTESY_ITEMS.find((i) => i.key === form.item);
  const period = validity({ startsAt: form.startsAt ? `${form.startsAt}T12:00:00-03:00` : null, endsAt: form.endsAt ? `${form.endsAt}T12:00:00-03:00` : null });

  return (
    <div className={s.formGrid}>
      <div>
        <SelectField
          classes={classes}
          s={s}
          id="exception-kind"
          label="Tipo"
          value={form.kind}
          onChange={(e) => set({ kind: e.target.value, item: e.target.value === "price" ? defaultPriceItem(summary) : DEFAULT_ITEM[e.target.value], value: "", userId: "" })}
          autoFocus
        >
          <MenuItem value="limit">Limite fora do plano</MenuItem>
          <MenuItem value="courtesy">Cortesia (não cobrar)</MenuItem>
          <MenuItem value="price">Preço negociado</MenuItem>
          <MenuItem value="discount">Desconto</MenuItem>
        </SelectField>

        {form.kind === "limit" ? (
          <div className={classes.fieldRow}>
            <SelectField classes={classes} s={s} id="exception-resource" label="Recurso" value={form.item} onChange={(e) => set({ item: e.target.value })}>
              {LIMIT_ITEMS.map(({ key, label }) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </SelectField>
            <div className={classes.field}>
              <label className={classes.fieldLabel} htmlFor="exception-value">Novo limite</label>
              <input
                id="exception-value"
                type="number"
                min={0}
                className={classes.fieldInput}
                value={form.value}
                onChange={(e) => set({ value: e.target.value })}
                placeholder="Sem limite"
              />
              <div className={classes.fieldHint}>Vazio: sem limite.</div>
            </div>
          </div>
        ) : form.kind === "price" || form.kind === "discount" ? (
          <div className={classes.fieldRow}>
            <SelectField classes={classes} s={s} id="exception-item" label="Item" value={form.item} onChange={(e) => set({ item: e.target.value })}>
              {(form.kind === "price" ? priceItems(summary) : DISCOUNT_ITEMS).map(({ key, label }) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </SelectField>
            <div className={classes.field}>
              <label className={classes.fieldLabel} htmlFor="exception-value">{form.kind === "price" ? "Preço por unidade (R$)" : "Desconto (%)"}</label>
              <input
                id="exception-value"
                inputMode={form.kind === "price" ? "decimal" : "numeric"}
                className={classes.fieldInput}
                value={form.value}
                onChange={(e) => set({ value: e.target.value })}
                placeholder={form.kind === "price" ? "Ex.: 79,90" : "Ex.: 15"}
              />
              <div className={classes.fieldHint}>
                {form.kind === "price" ? `Catálogo: ${money(listPrice(summary, form.item))} por unidade.` : "De 1% a 100%. Com outro desconto, vale o maior."}
              </div>
            </div>
          </div>
        ) : (
          <>
            <SelectField classes={classes} s={s} id="exception-courtesy" label="O que não cobrar" value={form.item} onChange={(e) => set({ item: e.target.value, userId: "" })}>
              {COURTESY_ITEMS.map(({ key, label }) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </SelectField>
            {form.item === "user_license" && (
              <SelectField classes={classes} s={s} id="exception-person" label="Pessoa" value={form.userId} onChange={(e) => set({ userId: e.target.value })}>
                {people.map((person) => <MenuItem key={person.id} value={String(person.id)}>{person.name} ({person.email})</MenuItem>)}
              </SelectField>
            )}
          </>
        )}

        <div className={classes.fieldRow}>
          <div className={classes.field}>
            <label className={classes.fieldLabel} htmlFor="exception-start">Começa em</label>
            <input id="exception-start" type="date" className={`${classes.fieldInput} ${s.dateInput}`} value={form.startsAt} onChange={(e) => set({ startsAt: e.target.value })} />
            <div className={classes.fieldHint}>Vazio: já começa.</div>
          </div>
          <div className={classes.field}>
            <label className={classes.fieldLabel} htmlFor="exception-end">Termina em</label>
            <input id="exception-end" type="date" className={`${classes.fieldInput} ${s.dateInput}`} value={form.endsAt} onChange={(e) => set({ endsAt: e.target.value })} />
            <div className={classes.fieldHint}>Vazio: até ser revogada.</div>
          </div>
        </div>

        <ReasonField classes={classes} s={s} value={form.reason} onChange={(reason) => set({ reason })} placeholder="Ex.: negociação comercial de 29/10" />
      </div>

      <aside className={s.effect} aria-live="polite" aria-label="Com a exceção">
        <div className={s.effectTitle}>Com a exceção</div>
        {form.kind === "limit" && resource ? (
          <div className={s.effectGrid}>
            <span>{resource.label}</span>
            <span>
              {(() => {
                const limit = form.value === "" ? null : Number(form.value);
                return RESOURCES.some((r) => r.key === resource.key) ? usedOfLimit(summary.usage[resource.key], limit) : limitText(limit).toLowerCase();
              })()}
            </span>
            <span>Hoje</span>
            <span>{limitText(summary.limits[resource.key]).toLowerCase()}</span>
            <span>Prazo</span>
            <span>{period.toLowerCase()}</span>
          </div>
        ) : form.kind === "price" ? (
          <div className={s.effectGrid}>
            <span>{(priceItems(summary).find((i) => i.key === form.item) || priceItems(summary)[0]).label}</span>
            <span>{centsOf(form.value) != null ? `${money(centsOf(form.value))} por unidade` : "-"}</span>
            <span>Catálogo</span>
            <span>{money(listPrice(summary, form.item))}</span>
            <span>Prazo</span>
            <span>{period.toLowerCase()}</span>
          </div>
        ) : form.kind === "discount" ? (
          <div className={s.effectGrid}>
            <span>Desconto</span>
            <span>{percentOf(form.value) != null ? `${percentOf(form.value)}%` : "-"}</span>
            <span>Em</span>
            <span>{(DISCOUNT_ITEMS.find((i) => i.key === form.item) || DISCOUNT_ITEMS[0]).label.toLowerCase()}</span>
            <span>Prazo</span>
            <span>{period.toLowerCase()}</span>
          </div>
        ) : (
          <div className={s.effectGrid}>
            <span>Não cobrar</span>
            <span>{courtesy ? courtesy.label.toLowerCase() : ""}</span>
            <span>Prazo</span>
            <span>{period.toLowerCase()}</span>
          </div>
        )}
        <div className={s.effectNote}>
          {form.kind === "limit"
            ? "Vale só nesta Empresa e passa por cima do limite do plano enquanto durar."
            : "Muda a cobrança, não os limites: vale a partir da próxima renovação (o mês já pago não muda) e, na hora, na contratação e no proporcional das alterações. O catálogo não muda."}
        </div>
      </aside>
    </div>
  );
};

/** Uma entrada do histórico: quando, o quê, quem, o detalhe, o impacto, até quando vale, o motivo e o antes e depois. */
const HistoryItem = ({ s, entry, people }) => {
  const [open, setOpen] = useState(false);
  const rows = fieldRows(entry, people);
  const detail = historyDetail(entry);
  const blank = !!entry.before && !!entry.after && rows.some((row) => row.before === "" || row.after === "");
  return (
    <li className={s.historyItem} tabIndex={-1}>
      <div className={s.historyWhen}>{dateTime(entry.createdAt)}</div>
      <div className={s.historyWhat}>
        {ACTION_LABELS[entry.action] || entry.action}
        <span className={s.historyActor}>{entry.actor ? ` por ${entry.actor.name}` : " pelo sistema"}</span>
      </div>
      {detail && <div className={s.historyDetail}>{detail}</div>}
      {(entry.financialImpactCents || entry.effectiveUntil) && (
        <div className={s.historyMeta}>
          {entry.financialImpactCents ? <span>{impactText(entry)}</span> : null}
          {entry.effectiveUntil ? <span>{`Vale até ${date(entry.effectiveUntil)}`}</span> : null}
        </div>
      )}
      {entry.reason && <div className={s.historyReason}>"{entry.reason}"</div>}
      {rows.length > 0 && (
        <>
          <button type="button" className={s.historyToggle} onClick={() => setOpen((value) => !value)} aria-expanded={open}>
            {open ? "Esconder antes e depois" : "Ver antes e depois"}
          </button>
          {open && (
            <table className={s.historyFields}>
              {blank && <caption>Em branco: não registrado.</caption>}
              <thead>
                <tr>
                  <th scope="col"><span className={s.srOnly}>Campo</span></th>
                  <th scope="col">Antes</th>
                  <th scope="col">Depois</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.label}</th>
                    <td>{row.before}</td>
                    <td>{row.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </li>
  );
};

/** O histórico completo (etapa 11): por assunto, do mais novo para o mais antigo, em páginas. */
const HistoryPanel = ({ s, entries, hasMore, topic, loading, people, listRef, onTopic, onMore, onClose, closeRef }) => (
  <aside id="plan-history" className={s.history} aria-label="Histórico de mudanças">
    <div className={s.historyHead}>
      <span className={s.historyTitle}>Histórico de mudanças</span>
      <button ref={closeRef} className={s.tool} onClick={onClose} title="Fechar o histórico" aria-label="Fechar o histórico"><X size={18} /></button>
    </div>
    <div className={s.historyFilters} role="group" aria-label="Assunto">
      {HISTORY_TOPICS.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`${s.historyFilter}${topic === item.key ? ` ${s.historyFilterOn}` : ""}`}
          aria-pressed={topic === item.key}
          aria-disabled={loading}
          onClick={() => onTopic(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
    {entries.length === 0 ? (
      <div className={s.historyList}><div className={s.empty} style={{ paddingTop: 16 }}>{loading ? "Carregando..." : "Nenhuma mudança registrada."}</div></div>
    ) : (
      // A lista nasce de novo a cada assunto: começa do topo.
      <ol key={topic} ref={listRef} className={s.historyList} aria-busy={loading}>
        {entries.map((entry) => <HistoryItem key={entry.id} s={s} entry={entry} people={people} />)}
        {hasMore && (
          <li className={s.historyMoreRow}>
            <button type="button" className={s.historyMore} onClick={onMore} aria-disabled={loading}>
              {loading ? "Carregando..." : "Carregar mais"}
            </button>
          </li>
        )}
      </ol>
    )}
  </aside>
);

const CompanyPlanModal = ({ classes, companyGroupId, companyName, people, onClose, onChanged }) => {
  const s = useStyles();
  const [summary, setSummary] = useState(null);
  const [failed, setFailed] = useState(false);
  const [view, setView] = useState("summary");
  const [planDraft, setPlanDraft] = useState(null);
  const [grantDraft, setGrantDraft] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // O histórico completo (etapa 11): começa pelas entradas do resumo; o assunto e o "carregar mais" pedem ao servidor.
  const [history, setHistory] = useState({ entries: [], hasMore: false, topic: "all", loading: false });
  // Só a resposta da última pergunta entra: a que chega depois de outra (ou do resumo relido) fica de fora.
  const historyTicket = useRef(0);
  // Depois do "carregar mais", o foco vai para a primeira entrada nova (na última página, o botão some).
  const historyFocusIndex = useRef(null);
  const historyListRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const historyButtonRef = useRef(null);
  const historyCloseRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/company-groups/${companyGroupId}/commercial`);
      setSummary(data);
      setFailed(false);
    } catch (err) {
      setFailed(true);
      toastError(err);
    }
  }, [companyGroupId]);

  useEffect(() => { load(); }, [load]);

  // O resumo relido (depois de cada mudança) traz as entradas mais novas de tudo.
  useEffect(() => {
    if (!summary) return;
    historyTicket.current += 1;
    setHistory({ entries: summary.history || [], hasMore: !!summary.historyHasMore, topic: "all", loading: false });
  }, [summary]);

  useEffect(() => {
    const index = historyFocusIndex.current;
    if (index === null) return;
    historyFocusIndex.current = null;
    const item = historyListRef.current && historyListRef.current.children[index];
    if (item) item.focus();
  }, [history.entries]);

  const fetchHistory = async (topic, before, firstNew) => {
    historyTicket.current += 1;
    const ticket = historyTicket.current;
    setHistory((current) => ({ ...current, loading: true }));
    try {
      const params = new URLSearchParams({ topic, ...(before ? { before: String(before) } : {}) });
      const { data } = await api.get(`/company-groups/${companyGroupId}/history?${params.toString()}`);
      if (ticket !== historyTicket.current) return;
      if (before) historyFocusIndex.current = firstNew;
      setHistory((current) => ({
        entries: before ? [...current.entries, ...data.entries] : data.entries,
        hasMore: data.hasMore,
        topic,
        loading: false
      }));
    } catch (err) {
      if (ticket !== historyTicket.current) return;
      toastError(err);
      setHistory((current) => ({ ...current, loading: false }));
    }
  };
  const chooseTopic = (topic) => {
    if (!history.loading && topic !== history.topic) fetchHistory(topic);
  };
  const moreHistory = () => {
    const last = history.entries[history.entries.length - 1];
    if (!history.loading && last) fetchHistory(history.topic, last.id, history.entries.length);
  };

  // Foco no modal ao abrir e de volta em quem abriu ao fechar; a página atrás não rola.
  useEffect(() => {
    const opener = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (cardRef.current) cardRef.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (opener && opener.focus) opener.focus();
    };
  }, []);

  useEffect(() => {
    if (historyOpen && historyCloseRef.current) historyCloseRef.current.focus();
  }, [historyOpen]);

  const closeHistory = () => {
    setHistoryOpen(false);
    if (historyButtonRef.current) historyButtonRef.current.focus();
  };
  const close = () => { if (!busy) onClose(); };

  // Esc fecha o histórico; sem ele, o modal. O Esc de um menu de select aberto
  // (fora do modal na página) não fecha nada além do menu.
  const onKeyDown = (e) => {
    if (e.key !== "Escape" || !cardRef.current || !cardRef.current.contains(e.target)) return;
    e.stopPropagation();
    if (historyOpen) closeHistory();
    else close();
  };

  const saved = (data) => {
    setSummary(data);
    setView("summary");
    setBusy(false);
    if (onChanged) onChanged();
  };

  const openPlan = () => { setPlanDraft(planForm(summary)); setView("plan"); };
  const openGrant = () => { setGrantDraft(grantForm()); setView("grant"); };
  const setPlan = (patch) => setPlanDraft((current) => ({ ...current, ...patch }));
  const setGrant = (patch) => setGrantDraft((current) => ({ ...current, ...patch }));

  const savePlan = async () => {
    const planKey = planDraft.plan !== NONE ? planDraft.plan : null;
    setBusy(true);
    try {
      const { data } = await api.put(`/company-groups/${companyGroupId}/commercial`, {
        plan: planKey,
        enforcementMode: planKey ? planDraft.enforcementMode : "off",
        seatsContracted: planDraft.seatsContracted === "" ? null : Number(planDraft.seatsContracted),
        internalChatSeats: planDraft.internalChatSeats === "" ? null : Number(planDraft.internalChatSeats),
        aiPackMessages: planDraft.aiPackMessages === NONE ? null : Number(planDraft.aiPackMessages),
        reason: planDraft.reason
      });
      toast.success("Plano salvo");
      saved(data);
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };

  const saveGrant = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/company-groups/${companyGroupId}/exceptions`, {
        kind: grantDraft.kind,
        // O preço vai como o item e para quê ("plan:pro" = as licenças do Pro).
        item: grantDraft.kind === "price" ? grantDraft.item.split(":")[0] : grantDraft.item,
        appliesTo: grantDraft.kind === "price" ? grantDraft.item.split(":")[1] || null : null,
        value:
          grantDraft.kind === "limit"
            ? grantDraft.value !== "" ? Number(grantDraft.value) : null
            : grantDraft.kind === "price"
              ? centsOf(grantDraft.value)
              : grantDraft.kind === "discount"
                ? percentOf(grantDraft.value)
                : null,
        userId: grantDraft.item === "user_license" && grantDraft.userId ? Number(grantDraft.userId) : null,
        startsAt: grantDraft.startsAt || null,
        endsAt: grantDraft.endsAt || null,
        reason: grantDraft.reason
      });
      toast.success("Exceção concedida");
      saved(data);
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };

  const formTitle = view === "plan" ? (summary && summary.group.plan ? "Alterar plano" : "Escolher plano") : "Conceder exceção";
  const canSave = view === "plan"
    ? planDraft && planDraft.reason.trim().length >= 3
    : grantDraft &&
      grantDraft.reason.trim().length >= 3 &&
      !(grantDraft.item === "user_license" && !grantDraft.userId) &&
      !(grantDraft.kind === "price" && centsOf(grantDraft.value) == null) &&
      !(grantDraft.kind === "discount" && percentOf(grantDraft.value) == null);

  return createPortal(
    <div className={`${classes.modalBackdrop} ${s.backdrop}`} onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div
        ref={cardRef}
        className={`${classes.modalCard} ${s.card}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-modal-title"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className={s.header}>
          <div className={s.headerText}>
            <div id="plan-modal-title" className={classes.modalTitle}>Plano e limites</div>
            <div className={s.headerSub}>{companyName}</div>
          </div>
          <div className={s.tools}>
            {summary && (
              <Tooltip title="Histórico de mudanças" arrow placement="bottom">
                <button
                  ref={historyButtonRef}
                  className={`${s.tool}${historyOpen ? ` ${s.toolActive}` : ""}`}
                  onClick={() => (historyOpen ? closeHistory() : setHistoryOpen(true))}
                  aria-label="Histórico de mudanças"
                  aria-expanded={historyOpen}
                  aria-controls="plan-history"
                >
                  <History size={18} />
                </button>
              </Tooltip>
            )}
            <button className={s.tool} onClick={close} title="Fechar" aria-label="Fechar"><X size={18} /></button>
          </div>
        </header>

        <div className={s.body}>
          {!summary && (
            <div className={s.loading}>
              {failed ? (
                <>Não deu para carregar o plano da Empresa. <button className={s.textBtn} onClick={load}>Tentar de novo</button></>
              ) : "Carregando..."}
            </div>
          )}
          {summary && view === "summary" && (
            <SummaryView classes={classes} s={s} summary={summary} busy={busy} setBusy={setBusy} onEditPlan={openPlan} onGrant={openGrant} onSaved={saved} onReload={load} />
          )}
          {summary && view !== "summary" && (
            <div className={s.view} key={view}>
              <div className={s.viewHead}>
                <button className={s.tool} onClick={() => setView("summary")} disabled={busy} title="Voltar" aria-label="Voltar"><ArrowLeft size={18} /></button>
                <span className={s.viewTitle}>{formTitle}</span>
              </div>
              {view === "plan" && <PlanView classes={classes} s={s} summary={summary} form={planDraft} set={setPlan} />}
              {view === "grant" && <GrantView classes={classes} s={s} summary={summary} people={people} form={grantDraft} set={setGrant} />}
            </div>
          )}
        </div>

        {summary && view !== "summary" && (
          <footer className={s.footer}>
            <button className={classes.ghostBtn} onClick={() => setView("summary")} disabled={busy}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={view === "plan" ? savePlan : saveGrant} disabled={busy || !canSave}>
              {view === "plan" ? (busy ? "Salvando..." : "Salvar plano") : (busy ? "Concedendo..." : "Conceder")}
            </button>
          </footer>
        )}

        {historyOpen && summary && (
          <>
            <div className={s.veil} onMouseDown={closeHistory} aria-hidden="true" />
            <HistoryPanel
              s={s}
              entries={history.entries}
              hasMore={history.hasMore}
              topic={history.topic}
              loading={history.loading}
              people={people}
              listRef={historyListRef}
              onTopic={chooseTopic}
              onMore={moreHistory}
              onClose={closeHistory}
              closeRef={historyCloseRef}
            />
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CompanyPlanModal;
